import { useCallback, useMemo } from 'react';
import { batch, useDispatch } from 'react-redux';
import { Logger } from '@saviynt/common';
import { type MenuOption } from '@saviynt/design-system';

import {
  ADVANCED_FILTERS_METHODS,
  DEFAULT_AD_FILTER_PARAMS,
  DEFAULT_FILTERS,
  ENTITLEMENT_DEFAULT_FILTERS,
  PERSONALIZATION_DEFAULT_CONFIG,
  RENDER_MODES,
  VIOLATIONS_TAB_LIST_AD_FILTER,
  VIOLATIONS_TAB_LIST_INITIAL_FILTERS,
} from '../../../shared/constants';
import {
  useInvertedLocalize,
  usePersonalization,
  useTypedSelector,
} from '../../../shared/hooks';
import {
  type ActiveFiltersState,
  type AdvancedFilterState,
} from '../../../shared/types';
import { createDevRejectionHandler } from '../../../shared/utils';
import { useGetApplicationEndpointsMutation } from '../data-layer/violationsTabApi';
import { violationsTabActions } from '../data-store/violationsTabSlice';

import { useFilterCache } from './use-filtercache';
import { useGetQueryMap } from './use-getQueryMap';

/**
 * Interface representing a Promise that can be unwrapped
 * @template T The type of value the promise resolves to
 */
interface UnwrappablePromise<T> extends Promise<T> {
  unwrap(): Promise<T>;
}

/**
 * Type definition for the query map object
 * Maps filter keys to their corresponding query functions
 */
type QueryMapType = Record<
  string,
  (params: typeof DEFAULT_AD_FILTER_PARAMS) => UnwrappablePromise<unknown>
>;

/**
 * Interface representing a query item with its key, function and parameters
 */
interface QueryItem {
  key: string;
  fn: (params: typeof DEFAULT_AD_FILTER_PARAMS) => UnwrappablePromise<unknown>;
  params: typeof DEFAULT_AD_FILTER_PARAMS;
}

/**
 * Interface for mapping query responses to success and error states
 */
interface ResponseMap {
  success: Record<string, unknown>;
  errors: Record<string, unknown>;
}

/**
 * Custom hook for initializing and managing advanced filters
 * @returns {Object} Object containing the initializeAdvancedFilters function
 *
 * @example
 * const { initializeAdvancedFilters } = useFiltersInitialization(
 *   queryMap,
 *   translateFunction
 * );
 */
const useFiltersInitialization = (
  currentActiveFilters: ActiveFiltersState[] = []
) => {
  const l = useInvertedLocalize();
  const dispatch = useDispatch();

  const { filterCache, filters } = useTypedSelector(
    (state) => state.aag.violationsTab.controls
  );

  // Hooks
  const queryMap = useGetQueryMap(true) as QueryMapType;
  const { getPersonlization } = usePersonalization();
  const { setGroupInitialCache, setCache } = useFilterCache(
    filterCache,
    violationsTabActions.setFiltersCache
  );
  const [fetchApplicationEndpoints] = useGetApplicationEndpointsMutation();

  // Constants
  const savedFilters = useMemo(() => {
    const personalization = getPersonlization();

    const activeFilterMap: Record<string, string[][]> = {};

    currentActiveFilters.forEach((f) => {
      activeFilterMap[f.value] = f.options.map((o) => [o.value, o.label]);
    });

    return PERSONALIZATION_DEFAULT_CONFIG.saveFilter &&
      personalization &&
      personalization.filters
      ? personalization.filters
      : activeFilterMap;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- dont need to depend upon `currentActiveFilters`
  }, [getPersonlization]);
  const isListRenderMode = useMemo(
    () => getPersonlization()?.mode === RENDER_MODES.LIST,
    [getPersonlization]
  );

  const hydrateApplicationFilter = useCallback(
    async (filtersSnapshot: AdvancedFilterState[]) => {
      const applicationFilterKey =
        VIOLATIONS_TAB_LIST_AD_FILTER.APPLICATION.value;
      const applicationFilter = filtersSnapshot.find(
        (filter) => filter.value === applicationFilterKey
      );

      if (
        !applicationFilter ||
        (applicationFilter.options && applicationFilter.options.length > 0)
      ) {
        return;
      }

      try {
        const response = await fetchApplicationEndpoints(
          DEFAULT_AD_FILTER_PARAMS
        ).unwrap();
        const options = Array.isArray(response)
          ? (response as MenuOption[])
          : [];

        if (options.length === 0) {
          return;
        }

        setCache(
          applicationFilterKey,
          DEFAULT_AD_FILTER_PARAMS.search,
          options
        );

        dispatch(
          violationsTabActions.hydrateFilterOptions({
            filterKey: applicationFilterKey,
            options,
          })
        );
      } catch (error) {
        Logger.log(
          'warn',
          'Failed to prefetch Application filter options',
          error
        );
      }
    },
    [dispatch, fetchApplicationEndpoints, setCache]
  );

  const initializeAdvancedFilters = useCallback(async (): Promise<boolean> => {
    const queryArr: QueryItem[] = [];

    Object.entries(queryMap).forEach(([value, fn]) => {
      queryArr.push({
        key: value,
        fn,
        params: DEFAULT_AD_FILTER_PARAMS,
      });
    });

    try {
      const results = await Promise.allSettled(
        queryArr.map(({ params: params_2, fn }) => fn(params_2).unwrap())
      );
      // Mapping response to success and error
      const responseMap = results.reduce<ResponseMap>(
        (acc, result_1, index) => {
          const { key } = queryArr[index];

          if (result_1.status === 'fulfilled') {
            acc.success[key] = result_1.value;

            return acc;
          }

          acc.errors[key] = result_1.reason;

          return acc;
        },
        { success: {}, errors: {} }
      );

      // If any filter api fails then throw exception
      const erroredFilters = Object.keys(responseMap.errors);

      if (erroredFilters.length > 0) {
        throw Error(`Error in fetching filter(s) ${erroredFilters.join('')}`);
      }

      const cacheObjects: Record<string, MenuOption[]> = {};
      const savedObjects: ActiveFiltersState[] = [];
      const isEntitlementContext =
        window.location.pathname.includes('entitlement');

      // Get the correct filter order based on context
      const filtersToProcess = isEntitlementContext
        ? VIOLATIONS_TAB_LIST_INITIAL_FILTERS
        : Object.values(VIOLATIONS_TAB_LIST_AD_FILTER);

      // Configuring filters
      const initialFilters: AdvancedFilterState[] = filtersToProcess.map(
        (f) => {
          const options = [];
          let isDefaultFilter = Boolean(
            isEntitlementContext
              ? ENTITLEMENT_DEFAULT_FILTERS.includes(f.value)
              : DEFAULT_FILTERS.includes(f.value) ||
                  filters.find((_filter) => _filter.value === f.value)
                    ?.isChecked
          );
          const isOptionFromApi =
            f.method === ADVANCED_FILTERS_METHODS.API_WITH_OPTIONS &&
            responseMap.success[f.value];
          const isFixedOption =
            f.method === ADVANCED_FILTERS_METHODS.CUSTOM ||
            f.method === ADVANCED_FILTERS_METHODS.FIXED;
          const isPersonalized = Boolean(Array.isArray(savedFilters[f.value]));

          // Check for options in initial APIs
          if (isOptionFromApi) {
            const successResponse = responseMap.success[
              f.value
            ] as MenuOption[];

            options.push(...successResponse);

            cacheObjects[f.value] = successResponse;
          }

          if (isFixedOption && f.options) {
            const localizedOptions = f.options.map((option) => ({
              ...option,
              label: l(option.label),
            }));

            options.push(...localizedOptions);
          }

          // Get options from personalization
          if (isPersonalized) {
            const savedOptions = savedFilters[f.value];
            const optionMap = new Map(
              options.map((option) => [option.value, option])
            );

            savedOptions.forEach(([value, label]) => {
              const existing = optionMap.get(value);

              if (existing) {
                optionMap.set(value, { ...existing, isChecked: true });
              } else {
                optionMap.set(value, { label, value, isChecked: true });
              }
            });
            const activeOptions = Array.from(optionMap.values()).filter(
              (option) => option.isChecked
            );

            savedObjects.push({
              value: f.value,
              method: f.method,
              options: activeOptions,
            });

            isDefaultFilter = true;
          }

          return {
            label: l(f.label),
            value: f.value,
            isDisabled: Boolean(f.isNoRemoveFilterBtn),
            isChecked: isDefaultFilter,
            method: f.method,
            category: f.category !== undefined ? l(f.category) : f.category,
            options,
            isNoRemoveFilterBtn: Boolean(f.isNoRemoveFilterBtn),
          };
        }
      );

      // Initial cache
      setGroupInitialCache(cacheObjects, DEFAULT_AD_FILTER_PARAMS.search);

      batch(() => {
        dispatch(violationsTabActions.setActiveFilters([...savedObjects]));
        dispatch(violationsTabActions.setAdvancedFilters(initialFilters));
      });

      hydrateApplicationFilter(initialFilters).catch(
        createDevRejectionHandler(
          'use-filterinitialization: hydrateApplicationFilter'
        )
      );

      return true;
    } catch (error) {
      Logger.log('Error fetching filters', error);

      return false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- should be one time dependency on `config`
  }, [
    l,
    queryMap,
    filters,
    savedFilters,
    isListRenderMode,
    dispatch,
    setGroupInitialCache,
    hydrateApplicationFilter,
  ]);

  return { initializeAdvancedFilters };
};

// eslint-disable-next-line import/prefer-default-export
export { useFiltersInitialization };
